// ---------------------------------------------------------------------------
// Data layer: EVERYTHING comes from the backend database. There is no sample
// or generated data here. When the database has nothing for a company, the
// page shows an empty table or "not available" - never made-up numbers.
// ---------------------------------------------------------------------------
import { API_URL as CONFIG_URL } from "./config.js";

export const API_URL = String(CONFIG_URL || "").replace(/\/+$/, "");
export const USE_API = !!API_URL;

// kept so older imports keep working; the "sample data" banner never shows
export const IS_SAMPLE = false;
export const isSampleNow = () => false;
export const loadMeta = () => Promise.resolve({ sample: false });
export const hasCompany = () => true;

// ---- helpers ---------------------------------------------------------------
const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;
const DATE_TIME = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/;
// dates arrive as text; turn them into Dates on the same calendar day / exchange clock time
const revive = (v) => {
  if (typeof v === "string") {
    let m = DATE_ONLY.exec(v);
    if (m) return new Date(+m[1], +m[2] - 1, +m[3]);
    m = DATE_TIME.exec(v);
    if (m) return new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]);
    return v;
  }
  if (Array.isArray(v)) return v.map(revive);
  if (v && typeof v === "object") {
    const o = {};
    for (const k of Object.keys(v)) o[k] = revive(v[k]);
    return o;
  }
  return v;
};

const enc = encodeURIComponent;
const T = (t) => String(t || "").toUpperCase();

/** GET <API_URL><path>; null when the database has nothing (or the server is down) */
function get(path) {
  if (!USE_API) return Promise.resolve(null);
  return fetch(API_URL + path)
    .then((r) => (r.ok ? r.json() : null))
    .then((j) => (j && !j.error ? revive(j) : null))
    .catch(() => null);
}
const stock = (t, name, qs = "") => get(`/stocks/${enc(T(t))}/${name}${qs}`);
const orEmpty = (empty) => (v) => (v === null ? empty : v);

// ---- search + profile ------------------------------------------------------
export function searchStocks(query, limit = 8) {
  const q = String(query || "").trim();
  if (!q) return Promise.resolve([]);
  return get(`/search?q=${enc(q)}&limit=${limit}`).then((r) => r || []);
}

export function getProfile(t) {
  return stock(t, "profile").then((db) => {
    if (!db) return null; // company not in the database -> "couldn't find"
    const ex = db.exchange ? String(db.exchange).toUpperCase() : "";
    const classified = (v) => (v && v !== "Unclassified" ? v : "—");
    return {
      ticker: db.ticker,
      name: db.name,
      exchange: ex,
      tvSymbol: ex ? `${ex}:${db.ticker}` : db.ticker,
      tvUrl: `https://www.tradingview.com/symbols/${ex ? ex + "-" : ""}${enc(db.ticker)}/`,
      sector: classified(db.sector),
      industry: classified(db.industry),
      group: classified(db.industry_group || db.industry),
      country: db.country,
      currency: db.currency,
      employees: db.employees || null,
      founded: db.founded || null,
      website: db.website || null, hq: db.hq || null, phone: db.phone || null, sicDescription: db.sicDescription || null, fiscalYearEnd: db.fiscalYearEnd || null, aboutSource: db.aboutSource || null, aboutUrl: db.aboutUrl || null,
      description: db.description || `${db.name}${ex ? ` (${ex}:${db.ticker})` : ""} is a publicly traded company${ex ? ` listed on ${ex}` : ""}.`,
    };
  });
}

// ---- prices (Yahoo -> database) --------------------------------------------
export const getQuote = (t) => stock(t, "quote");
export const getCandles = (t, range) =>
  stock(t, "candles", `?range=${enc(range)}`).then(orEmpty({ interval: "", intraday: false, bars: [] }));
export const getPriceRange = () => Promise.resolve([]);

// ---- financials + filings (SEC -> database) --------------------------------
export const getStatement = (t, statement, period) =>
  stock(t, "statement", `?statement=${enc(statement)}&period=${enc(period)}`).then(orEmpty({ columns: [], rows: [] }));
export const getSnapshot = (t) => stock(t, "snapshot").then(orEmpty([]));
export const getKeyStats = (t) => stock(t, "key-stats").then(orEmpty([]));
export const getProsCons = (t) =>
  stock(t, "pros-cons").then(orEmpty({ pros: ["Financial data not loaded for this company yet."], cons: ["Financial data not loaded for this company yet."] }));
export const getFilings = (t) => stock(t, "filings").then(orEmpty([]));
export const getNews = (t) => stock(t, "news").then(orEmpty([]));

// ---- not available yet (needs a paid data vendor): empty, never fake --------
export const getValuation = () => Promise.resolve({ multiples: [], targets: null, competitors: [] });
export const getEstimates = () => Promise.resolve({ years: [], lastQs: [], nextQs: [] });
export const getTranscripts = () => Promise.resolve([]);
export const getOwnership = () => Promise.resolve({ holders: [], insiders: [] });

// ---- lists (Companies / Screens / Watchlist pages) -------------------------
export const getSummaries = () => Promise.resolve([]);
export const listCompanies = () => Promise.resolve({ total: 0, rows: [] });